import { Scene } from './Scene';
import { CloudVolume } from './cloud-volume/CloudVolume';
import { buildFlameFromRef } from './loaders/FlameLoader';
import { SoundEmitterRuntime } from '../audio/SoundEmitterRuntime';
import { SceneManifest } from './SceneManifest';
import { BoundingBox } from '../util';
import { RenderObject } from '../object/RenderObject';
import { SceneLoadProgressTracker } from './SceneLoadProgress';
import { loadTextures } from './loaders/TextureLoader';
import { loadModels } from './loaders/ModelLoader';
import { loadObjects } from './loaders/ObjectLoader';
import { TiledObjectStreamer } from './TiledObjectStreamer';
import { loadLights } from './loaders/LightLoader';
import { loadSkybox } from './loaders/SkyLoader';
import { loadNavigationMesh, loadNavigationInitialTiles } from './loaders/NavigationLoader';
import { loadTiledTerrain } from './TerrainLoader';
import { loadEffects } from './loaders/EffectLoader';
import { loadFeatures } from './loaders/FeatureLoader';
import { loadObjectPhysics, setupIk } from './loaders/PhysicsLoader';
import { registerLods } from './loaders/LodRegistrar';
import {
  instantiateComposite,
  manifestDefToEngineDef,
  manifestPlacementToEngine,
} from '../composite';

export type { ParsedModel } from './loaders/ModelLoader';

export class SceneManifestLoader {
  constructor(private scene: Scene) {}

  async load(
    url: string,
    tracker?: SceneLoadProgressTracker,
  ): Promise<{ manifest: SceneManifest; namedObjects: Map<string, RenderObject[]> }> {
    tracker?.beginStage('manifest');
    const manifest = await this.fetchManifest(url);

    // World bounds drive both the public scene.boundingBox and the
    // ObjectManager's spatial index. Init the octree before anything calls
    // addObject — objects with culling: 'octree' would otherwise throw.
    // Padding makes sure objects whose AABBs extend slightly past terrain
    // bounds (e.g. tall trees, decals) still fit inside a branch region.
    const min = manifest.boundingBox.min;
    const max = manifest.boundingBox.max;
    const pad = 100;
    this.scene.boundingBox = new BoundingBox(min, max);
    // Auto-scale leaf size to world size: 8km world → ~40m leaves, 250m
    // world → 10m leaves (the historical default). Larger leaves cut
    // internal-node count by O(N) levels in the octree query, which is the
    // dominant cost for camera-frustum culling on scatter-heavy worlds.
    // Floor at 10m so small/indoor scenes keep fine-grained leaves.
    const worldSpan = Math.max(max[0] - min[0], max[2] - min[2]);
    const minimumLeaf = Math.max(10, worldSpan / 200);
    this.scene.objectManager.initOctree(
      new BoundingBox(
        [min[0] - pad, min[1] - pad, min[2] - pad],
        [max[0] + pad, max[1] + pad, max[2] + pad],
      ),
      minimumLeaf,
    );

    tracker?.beginStage('textures');
    await loadTextures(this.scene, manifest, tracker);

    tracker?.beginStage('models');
    const modelCache = await loadModels(this.scene, manifest, tracker);
    this.scene.modelRegistry = modelCache;

    tracker?.beginStage('objects');
    const namedObjects = await loadObjects(this.scene, manifest, modelCache);
    registerLods(this.scene, manifest, namedObjects);

    tracker?.beginStage('lights');
    // If the manifest has tiled lights, only the directional lights live in manifest.lights[];
    // point/spot lights stream per-tile via the LIGHTS chunk in TiledObjectStreamer.
    loadLights(this.scene, manifest);

    tracker?.beginStage('skybox');
    await loadSkybox(this.scene, manifest);

    tracker?.beginStage('navigation');
    const spawn = manifest.spawnPoint;
    const spawnX = spawn?.[0] ?? 0;
    const spawnZ = spawn?.[2] ?? 0;
    // Constructs nav infrastructure but defers spatial-grid initial-tile
    // loading until after terrain so the BVH generator's terrain fetcher
    // sees a fully-loaded `TiledTerrainStreamer`.
    await loadNavigationMesh(
      this.scene,
      manifest,
      spawnX,
      spawnZ,
      this.scene.tileParseWorker ?? undefined,
    );

    // Terrain. The streamer also wires `setTerrainFallback` for any (x, z) the
    // spatial grid lacks coverage for — used in out-of-ring queries.
    tracker?.beginStage('terrain');
    if (manifest.terrain) {
      this.scene.terrainStreamer = await loadTiledTerrain(
        this.scene,
        manifest.terrain,
        spawnX,
        spawnZ,
        { terrainGenWorker: this.scene.terrainGenWorker ?? undefined },
      );
      if (this.scene.spatialGrid && this.scene.terrainStreamer) {
        const streamer = this.scene.terrainStreamer;
        this.scene.spatialGrid.setTerrainFallback((x, z) => streamer.sampleHeightWithNormal(x, z));
      }
    }

    // Block on the inner 3×3 of nav tiles. Sequenced after terrain so the BVH
    // generator's late-bound terrain fetcher sees a fully-loaded
    // `TiledTerrainStreamer`.
    await loadNavigationInitialTiles(this.scene, spawnX, spawnZ);

    // Composite defs must be populated BEFORE the streamer starts loading tiles —
    // the COMPOSITES chunk parser looks defs up by id and silently drops entries
    // when the map is empty. Manifest-level (out-of-bounds / global) placements
    // also instantiate from here; tile-routed placements go through the streamer.
    this.scene.compositeDefs.clear();
    for (const [id, def] of Object.entries(manifest.compositeDefs ?? {})) {
      this.scene.compositeDefs.set(id, manifestDefToEngineDef(def));
    }
    for (const placement of manifest.composites ?? []) {
      const def = this.scene.compositeDefs.get(placement.compositeId);
      if (!def) continue;
      instantiateComposite(this.scene, def, manifestPlacementToEngine(placement));
    }

    // Tile-streamed objects ride on the navmesh streamer's `.tile` archive cache
    // so each tile is fetched once even though navmesh, volgrid, and objects
    // all consume different chunks. Block on the inner 3×3; outer ring fills in
    // the background while the rest of scene activation continues.
    if (manifest.tiledObjects && this.scene.navMeshStreamer) {
      const objStreamer = new TiledObjectStreamer(
        this.scene,
        manifest.tiledObjects,
        modelCache,
        this.scene.navMeshStreamer.getArchiveCache(),
      );
      this.scene.objectStreamer = objStreamer;
      await objStreamer.loadInitialTiles(spawnX, spawnZ);
      // Far-objects bundle (Phase 2 of tile streaming) — one whole-world fetch
      // that spawns billboards for everything beyond the near 5×5. Loaded after
      // the near ring so we know which tiles to keep dormant.
      if (manifest.tiledObjects.farObjectsPath) {
        await objStreamer.loadFarObjects(manifest.tiledObjects.farObjectsPath);
      }
    }

    tracker?.beginStage('effects');
    await loadEffects(this.scene, manifest, tracker);

    // Cloud volumes are tiny POJOs (no GPU upload, no async work). Push (not
    // assign) so tile-streamed clouds already added by loadInitialTiles above
    // are not wiped. Tiled scenes will have manifest.cloudVolumes = [] anyway.
    for (const v of manifest.cloudVolumes ?? []) {
      this.scene.cloudVolumes.push(
        new CloudVolume({
          id: v.id,
          name: v.name,
          polygon: v.polygon.map((p) => [p[0], p[1]]),
          baseY: v.baseY,
          topY: v.topY,
          edgeFeather: v.edgeFeather,
          density: v.density,
          coverage: v.coverage,
          noiseScale: v.noiseScale,
          windDirX: v.windDirX,
          windDirZ: v.windDirZ,
          windSpeed: v.windSpeed,
        }),
      );
    }

    // Standalone flames. Push (not assign) so tile-streamed flames added above
    // are preserved; tiled scenes will have manifest.flames = [] anyway.
    for (const f of manifest.flames ?? []) {
      this.scene.flames.push(buildFlameFromRef(f));
    }

    // Manifest-level sound emitters: out-of-bounds + non-tiled scenes. Tiled
    // emitters install through TiledObjectStreamer's SOUND_EMITTERS path.
    // Acquire clips here (mirrors the streamer's per-tile acquire) so the
    // runtime's retry loop and spawn-mode playOneShot hit a warm cache without
    // depending on each engine consumer remembering to preload. Symmetric
    // release lives in `Scene.detached()` walking `manifestSoundEmitterClipIds`.
    const lib = this.scene.ctx.engine.audio.library;
    for (const e of manifest.soundEmitters ?? []) {
      void lib.acquire(e.clipId).catch(() => {});
      this.scene.manifestSoundEmitterClipIds.push(e.clipId);
      const runtime = new SoundEmitterRuntime(this.scene.ctx.engine.audio, e);
      this.scene.soundEmitters.set(e.id, runtime);
    }

    tracker?.beginStage('features');
    await loadFeatures(this.scene, manifest);

    tracker?.beginStage('physics');
    loadObjectPhysics(this.scene, manifest, namedObjects);

    tracker?.beginStage('finalize');
    setupIk(this.scene, namedObjects);
    tracker?.complete();

    return { manifest, namedObjects };
  }

  private async fetchManifest(url: string): Promise<SceneManifest> {
    const data = await this.scene.ctx.resourceLoader.loadResource(url);
    const text = new TextDecoder().decode(data);
    return JSON.parse(text);
  }
}
